import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ErrorService } from '../../../services/error/error.service';
import { FileService } from '../../../services/file.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { DocumentResponse } from '../../jurisdiction.domain';
import { FacilitiesFormService } from '../facilities-form.service';
import { EnvironmentService } from '../../../services/environment.service';
import { JurisdictionService } from '../../jurisdiction.service';
import { FormTypesData } from '../../../domains/common.domain';
import { SectionIdentifier } from 'src/app/+shared/components/review-section/review-section.domain';
import { UrlSegments } from '../../jurisdiction.constants';
import { CommunicationService } from 'src/app/services/communication.service';
import { ValidationsService } from 'src/app/services/validations.service';
import { InstallationChangeType } from 'src/app/constants/app.constants';

@Component({
  selector: 'app-old-meter-data',
  styleUrls: ['./old-meter-data.component.scss'],
  templateUrl: 'old-meter-data.component.html',
})
export class OldMeterDataComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isReadOnly: boolean = false;
  @Input() currentFormType: FormTypesData;
  @Input() isDetailsView: boolean = false;
  @Input() oldMeterData: any = null;
  @Input() currentInstallationType: string;

  count: number = 1;
  metersObj: FormArray;
  meterFG: FormGroup;
  formSubscription: Subscription;
  infoBubblesData: {
    [key: string]: boolean;
  };
  showData: boolean = true;

  isMeterNumberRequired = this.environmentService.isMeterNumberRequiredOnFacilitiesData;
  applicationId: any;
  get oldMeterDataSectionIdentifier(): SectionIdentifier {
    if (this.currentFormType.key === 'heating') return SectionIdentifier.TechnicalData_OldMeterData_Heating;
    else if (this.currentFormType.key === 'water') return SectionIdentifier.TechnicalData_OldMeterData_Water;
    else if (this.currentFormType.key === 'power') return SectionIdentifier.TechnicalData_OldMeterData_Power;
    else if (this.currentFormType.key === 'gas') return SectionIdentifier.TechnicalData_OldMeterData_Gas;
    else return null;
  }

  constructor(
    private facilitiesFormService: FacilitiesFormService,
    private localStorageService: LocalStorageService,
    private fileService: FileService,
    private errorService: ErrorService,
    private environmentService: EnvironmentService,
    private jurisdictionService: JurisdictionService,
    private communicationService: CommunicationService
  ) {}

  ngOnInit(): void {
    this.applicationId = this.localStorageService.getItem('application_id');
    this.infoBubblesData = this.environmentService.getInfoBubblesData('facilitiesData') || null;
    this.prepareForm();
    this.jurisdictionService.setMediaType(this.currentFormType.key).subscribe(isPower => {
      this.showData = isPower;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    var currentFormTypeUpdateOnChanges = this.isReadOnly ? changes?.currentFormType?.firstChange : !changes?.currentFormType?.firstChange;
    var oldMeterDataUpdateOnChanges = this.isReadOnly ? changes?.oldMeterData?.firstChange : !changes?.oldMeterData?.firstChange;
    if ((changes?.currentFormType && currentFormTypeUpdateOnChanges) || (changes?.oldMeterData && oldMeterDataUpdateOnChanges)) {
      this.prepareForm();
    }
  }

  ngOnDestroy(): void {
    this.meterFG = null;
    this.metersObj = null;
    this.oldMeterData = [];
    this.formSubscription.unsubscribe();
  }

  get hideOldMeterAction(): boolean {
    return this.currentInstallationType != InstallationChangeType.SystemMerge || this.environmentService.hideOldMeterAction;
  }

  prepareForm(): void {
    this.meterFG = new FormGroup({
      meters: new FormArray([]),
    });
    this.metersObj = this.meterFG.get('meters') as FormArray;
    this.addMeter();

    this.facilitiesFormService.registerForComponentFormUpdate.next({
      component: 'oldMeterData',
      formData: this.meterFG.get('meters'),
    });

    this.formSubscription = this.meterFG.valueChanges.subscribe(() => {
      this.facilitiesFormService.registerForComponentFormUpdate.next({
        component: 'oldMeterData',
        formData: this.meterFG.get('meters'),
      });
    });
  }

  addMeter(newMeter: boolean = false, data: any = null): void {
    if (!newMeter && this.oldMeterData && this.oldMeterData.length) {
      this.oldMeterData.forEach((dataObj, index) => {
        this.addForm(dataObj, index);
      });
    } else {
      this.addForm(data, this.metersObj.controls.length);
    }
  }

  addForm(data?: any, index?: number): void {
    let meterFG = new FormGroup({
      meterId: new FormControl(data?.meterId || ''),
      installationId: new FormControl(data?.installationId || ''),
      meterNumber: new FormControl(data?.meterNumber || '', [...(this.isMeterNumberRequired ? [Validators.required] : [])]),
      ...(this.environmentService.showMeterReadingField && {
        meterReading: new FormControl(data?.meterReading || ''),
      }),
      ...(this.environmentService.showMeterImageUpload && {
        meterImageId: new FormControl(data?.meterImageId || ''),
        meterAction: new FormControl({ value: data?.meterAction || '', disabled: this.isReadOnly }),
        meterDocumentName: new FormControl(''),
      }),
    });
    this.metersObj.push(meterFG);
  }

  removeMeter(id: number): void {
    const meterId = this.metersObj.value[id].meterId;
    let deleteUrl: string;
    switch (this.currentFormType.index) {
      case 0:
        deleteUrl = UrlSegments.UpdateOldPowerMeter;
        break;
      case 1:
        deleteUrl = UrlSegments.UpdateOldGasMeter;
        break;
      case 2:
        deleteUrl = UrlSegments.UpdateOldWaterMeter;
        break;
      case 3:
        deleteUrl = UrlSegments.UpdateOldHeatingMeter;
        break;
    }
    if (meterId) {
      this.communicationService.deleteData(`${deleteUrl}=${meterId}`).subscribe(
        response => {
          this.errorService.throwErrorIfFailure(response, true);
          this.metersObj.removeAt(id);
        },
        error => {
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    } else {
      this.metersObj.removeAt(id);
    }
  }

  addMultipleMeterData() {
    const index = this.metersObj.controls.length - 1;
    const meterData = { ...(this.metersObj.controls[index] as FormGroup).value };
    meterData.meterId = '';
    meterData.installationId = '';
    for (let i = 0; i < this.count; i++) {
      this.addMeter(true, meterData);
    }
  }

  caretUp() {
    if (!this.count) {
      this.count = 1;
      return;
    }
    if (this.count >= 1 && this.count < 20) {
      this.count++;
    }
  }

  caretDown() {
    if (!this.count) {
      this.count = 1;
      return;
    }
    if (this.count > 1) {
      this.count--;
    }
  }

  counter(event: any) {
    if (event.key === 'Backspace') {
      return;
    }
    if (this.count <= 0 || this.count >= 21) {
      this.count = null;
    }
  }

  isFieldRequired(formControl: AbstractControl) {
    return ValidationsService.isFieldRequired(formControl);
  }

  isValid(): boolean {
    return this.meterFG.valid;
  }

  getFileName(index: number): string {
    return this.metersObj.value[index].meterDocumentName || '';
  }

  onImageUpload(event: any, uploader: any, index: number): void {
    const name = this.metersObj.value[index].meterDocumentName;
    this.fileService.uploadDocumentsForWorkflows(event, this.applicationId, 'other', name).subscribe(
      (response: DocumentResponse) => {
        this.metersObj.controls[index]
          .get('meterDocumentName')
          .setValue(event && event.currentFiles && event.currentFiles[0] && event.currentFiles[0].name);
        this.metersObj.controls[index].get('meterImageId').setValue(response.document[0].document_id);
        uploader.clear();
        this.errorService.throwErrorIfFailure(response, true);
      },
      error => {
        uploader.clear();
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  removeFile(index: number): void {
    this.fileService.removeWorkflowDocument(this.metersObj.value[index].meterImageId).subscribe(
      response => {
        this.metersObj.controls[index].get('meterDocumentName').setValue('');
        this.metersObj.controls[index].get('meterImageId').setValue('');
        this.errorService.throwErrorIfFailure(response, true);
      },
      error => {
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  downloadDocument(index: number): void {
    this.fileService.readWorkflowDocument(this.metersObj.value[index].meterImageId).subscribe((response: Blob) => {
      this.fileService.downloadDocument(response, this.metersObj.value[index].meterDocumentName);
    });
  }
}
